
import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Loader2, ShoppingCart, ChevronUp } from 'lucide-react';
import { UnitSystem } from '@/utils/unitConversion/types';
import DateSelectionGrid from './DateSelectionGrid';
import FamilySizeSettings from './FamilySizeSettings';

interface GenerateShoppingListFormProps {
  isLoadingDates: boolean;
  isLoadingShoppingList: boolean;
  dates: Date[];
  selectedDates: Date[];
  availableDates: Date[];
  adults: number;
  kids: number;
  mealPlansMap: Record<string, Array<{ 
    type: 'recipe' | 'composition';
    id: string;
    name: string;
    recipeIds?: string[];
    isQuickRecipe?: boolean;
    mealType: string;
    authorFirstName?: string;
    imageUrl?: string;
    storageImageUrl?: string;
    userImageUrl?: string;
  }>>;
  preferredUnits: UnitSystem;
  isCollapsed?: boolean;
  onUnitChange: (value: UnitSystem) => void;
  onAdultsChange: (value: number) => void;
  onKidsChange: (value: number) => void;
  onDateToggle: (date: Date) => void;
  onGenerate: () => void;
  onToggleCollapse?: () => void;
}

const GenerateShoppingListForm: React.FC<GenerateShoppingListFormProps> = ({
  isLoadingDates,
  isLoadingShoppingList,
  dates,
  selectedDates,
  availableDates,
  adults,
  kids,
  mealPlansMap,
  preferredUnits,
  isCollapsed,
  onUnitChange,
  onAdultsChange,
  onKidsChange,
  onDateToggle,
  onGenerate,
  onToggleCollapse,
}) => {
  return (
    <Card>
      {onToggleCollapse && (
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <h3 className="text-base font-medium">Shopping List Settings</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="h-8 w-8 p-0"
            >
              <ChevronUp className={`h-4 w-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </CardHeader>
      )}
      {!isCollapsed && (
        <CardContent className={onToggleCollapse ? "pt-0" : "pt-6"}>
        <div className="flex flex-col space-y-4">
          <div className="space-y-4">
            <Label className="text-sm font-medium">Select Dates with Meal Plans</Label>
            <DateSelectionGrid 
              isLoading={isLoadingDates}
              dates={dates}
              selectedDates={selectedDates}
              availableDates={availableDates}
              mealPlansMap={mealPlansMap}
              onDateToggle={onDateToggle}
            />
          </div>

          <FamilySizeSettings 
            adults={adults}
            kids={kids}
            preferredUnits={preferredUnits}
            onAdultsChange={onAdultsChange}
            onKidsChange={onKidsChange}
            onUnitChange={onUnitChange}
          />

          <Button
            onClick={onGenerate}
            disabled={isLoadingShoppingList || selectedDates.length === 0}
            className="w-full md:w-auto mt-4"
          >
            {isLoadingShoppingList ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <ShoppingCart className="mr-2 h-4 w-4" />
                Generate Shopping List
              </>
            )}
          </Button>
        </div>
        </CardContent>
      )}
    </Card>
  );
};

export default GenerateShoppingListForm;
